import { useState, useEffect, useCallback } from "react";
import { Search, Shuffle, MapPin } from "lucide-react";
import api from "../lib/api";
import { TAGS, GENDERS } from "../lib/constants";
import ProfileCard from "../components/ProfileCard";
import TipModal from "../components/TipModal";
import TipJar from "../components/TipJar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious,
} from "../components/ui/pagination";

export default function Discover() {
  const [data, setData] = useState({ profiles: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("random");
  const [gender, setGender] = useState("");
  const [loc, setLoc] = useState("");
  const [locInput, setLocInput] = useState("");
  const [tipTarget, setTipTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jar, setJar] = useState(null);
  const [jarOpen, setJarOpen] = useState(false);

  const loadJar = useCallback(() => api.get("/jar").then((r) => setJar(r.data)).catch(() => {}), []);
  useEffect(() => { loadJar(); }, [loadJar]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 25, q: search, tag, sort, gender, location: loc });
    const { data } = await api.get(`/profiles?${params}`);
    setData(data);
    setLoading(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page, search, tag, sort, gender, loc]);

  useEffect(() => { load(); }, [load]);

  const doSearch = (e) => { e.preventDefault(); setSearch(q); setPage(1); };
  const applyLoc = (e) => { e.preventDefault(); setLoc(locInput); setPage(1); };

  const pages = [];
  for (let i = 1; i <= data.total_pages; i++) {
    if (i === 1 || i === data.total_pages || Math.abs(i - page) <= 2) pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col items-center text-center">
        <span className="font-mono text-xs uppercase tracking-widest text-copper">Discovery</span>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-bronze mt-1">Meet the community</h1>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
        <p className="text-muted-foreground max-w-xl">{data.total} people are sharing their story and asking for a little help.</p>
      </div>

      <div className="mb-8 flex justify-center">
        <button onClick={() => setJarOpen(true)} data-testid="discover-jar-cta"
          className="group flex items-center gap-4 bg-card/70 border border-border rounded-full pl-3 pr-6 py-2.5 shadow-sm hover:shadow-md hover:border-copper/40 transition-all">
          <TipJar balance={jar?.balance || 0} className="w-12 h-auto shrink-0 drop-shadow-sm" />
          <div className="text-left">
            <p className="font-mono text-[11px] uppercase tracking-widest text-copper">Community Tip Jar</p>
            <p className="font-serif text-xl font-bold text-bronze tabular-nums leading-tight">
              ${jar ? jar.balance.toFixed(2) : "—"}
              <span className="text-xs font-sans font-normal text-muted-foreground ml-1.5">— drop in a penny</span>
            </p>
          </div>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={doSearch} className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories, names, needs…" className="pl-9 rounded-full" />
        </form>
        <div className="flex gap-2">
          <Button data-testid="sort-random" variant={sort === "random" ? "default" : "outline"}
            onClick={() => { setSort("random"); setPage(1); }}
            className={`rounded-full ${sort === "random" ? "bg-copper hover:bg-copper/90 text-white" : ""}`}>
            <Shuffle className="w-4 h-4 mr-1" /> Random
          </Button>
          <Button data-testid="sort-newest" variant={sort === "newest" ? "default" : "outline"}
            onClick={() => { setSort("newest"); setPage(1); }}
            className={`rounded-full ${sort === "newest" ? "bg-copper hover:bg-copper/90 text-white" : ""}`}>
            Newest
          </Button>
        </div>
      </div>

      <div className="bg-card/60 border border-border rounded-2xl p-3 mb-6" data-testid="connection-filters">
        <p className="text-xs font-mono uppercase tracking-widest text-copper mb-2">Find people you'd love to help</p>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex gap-1.5 flex-wrap">
            {GENDERS.map((g) => (
              <button key={g.value || "any"} data-testid={`gender-${g.value || "any"}`}
                onClick={() => { setGender(g.value); setPage(1); }}
                className={`text-xs font-medium px-3 py-1.5 rounded-full ${gender === g.value ? "bg-sage text-white" : "bg-secondary text-bronze hover:bg-accent"}`}>
                {g.label}
              </button>
            ))}
          </div>
          <form onSubmit={applyLoc} className="relative flex-1 md:max-w-xs">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="location-filter" value={locInput} onChange={(e) => setLocInput(e.target.value)}
              placeholder="Filter by city or state…" className="pl-9 rounded-full h-9" />
          </form>
          {(gender || loc) && (
            <button data-testid="clear-filters" onClick={() => { setGender(""); setLoc(""); setLocInput(""); setPage(1); }}
              className="text-xs text-copper underline underline-offset-2">Clear</button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 no-scrollbar">
        <button onClick={() => { setTag(""); setPage(1); }} data-testid="tag-all"
          className={`text-xs font-mono uppercase tracking-wide px-3 py-1.5 rounded-full ${!tag ? "bg-copper text-white" : "bg-secondary text-bronze"}`}>All</button>
        {TAGS.map((t) => (
          <button key={t} onClick={() => { setTag(t); setPage(1); }} data-testid={`tag-${t}`}
            className={`text-xs font-mono uppercase tracking-wide px-3 py-1.5 rounded-full ${tag === t ? "bg-copper text-white" : "bg-secondary text-bronze hover:bg-accent"}`}>#{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-secondary animate-pulse" />)}
        </div>
      ) : data.profiles.length === 0 ? (
        <p className="text-center text-muted-foreground py-20">No profiles match your search yet.</p>
      ) : (
        <div data-testid="discovery-profile-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {data.profiles.map((p) => <ProfileCard key={p.user_id} profile={p} onTip={setTipTarget} />)}
        </div>
      )}

      {data.total_pages > 1 && (
        <Pagination className="mt-10">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious data-testid="page-prev" onClick={() => page > 1 && setPage(page - 1)}
                className={page === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"} />
            </PaginationItem>
            {pages.map((pg, idx) => (
              <PaginationItem key={idx}>
                {pg === "…" ? <span className="px-2 text-muted-foreground">…</span> : (
                  <PaginationLink data-testid={`page-${pg}`} isActive={pg === page} onClick={() => setPage(pg)}
                    className="cursor-pointer">{pg}</PaginationLink>
                )}
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext data-testid="page-next" onClick={() => page < data.total_pages && setPage(page + 1)}
                className={page === data.total_pages ? "pointer-events-none opacity-40" : "cursor-pointer"} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <TipModal open={!!tipTarget} onClose={() => setTipTarget(null)} mode="individual" recipient={tipTarget} />
      <TipModal open={jarOpen} onClose={() => { setJarOpen(false); loadJar(); }} mode="community" />
    </div>
  );
}
